import axios from 'axios';
import { API_URL } from '../config/api';

export type AiAgentCapability = 'receptive' | 'campaign' | 'followup' | 'crm';

export interface UnifiedAiAgentDTO {
  id: number;
  sectorId: number;
  name: string;
  isEnabled: boolean;
  prompt: string;
  knowledgeBase: string;
  autoTransferEnabled: boolean;
  humanTransferRulesEnabled: boolean;
  humanTransferRules: string;
  aiApiKey: string;
  aiModel: string;
  inactivityTimeoutMinutes: number;
  capReceptive: boolean;
  capCampaign: boolean;
  capFollowup: boolean;
  capCrm: boolean;
  calendarEnabled: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateUnifiedAiAgentDTO {
  sectorId: number;
  name: string;
  isEnabled?: boolean;
  prompt?: string;
  knowledgeBase?: string;
  autoTransferEnabled?: boolean;
  humanTransferRulesEnabled?: boolean;
  humanTransferRules?: string;
  aiApiKey?: string;
  aiModel?: string;
  inactivityTimeoutMinutes?: number;
  capReceptive: boolean;
  capCampaign: boolean;
  capFollowup: boolean;
  capCrm: boolean;
  calendarEnabled?: boolean;
}

export interface SectorAiConfigDTO {
  sectorId: number;
  receptiveAiEnabled: boolean;
  defaultReceptiveAgentId: number | null;
  midnightAiReactivationEnabled: boolean;
}

const headers = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export async function listUnifiedAgents(
  sectorId: number,
  token: string,
  capability?: AiAgentCapability
): Promise<UnifiedAiAgentDTO[]> {
  let url = `${API_URL}/UnifiedAiAgent?sectorId=${sectorId}`;
  if (capability) url += `&capability=${capability}`;
  const res = await axios.get(url, { headers: headers(token) });
  return res.data?.data ?? [];
}

export async function createUnifiedAgent(
  dto: CreateUnifiedAiAgentDTO,
  token: string
): Promise<UnifiedAiAgentDTO> {
  const res = await axios.post(`${API_URL}/UnifiedAiAgent`, dto, { headers: headers(token) });
  return res.data.data;
}

export async function updateUnifiedAgent(
  id: number,
  sectorId: number,
  dto: Partial<CreateUnifiedAiAgentDTO>,
  token: string
): Promise<UnifiedAiAgentDTO> {
  const res = await axios.put(`${API_URL}/UnifiedAiAgent/${id}?sectorId=${sectorId}`, dto, {
    headers: headers(token),
  });
  return res.data.data;
}

export async function deleteUnifiedAgent(id: number, sectorId: number, token: string): Promise<void> {
  await axios.delete(`${API_URL}/UnifiedAiAgent/${id}?sectorId=${sectorId}`, {
    headers: headers(token),
  });
}

export async function getSectorAiConfig(sectorId: number, token: string): Promise<SectorAiConfigDTO> {
  const res = await axios.get(`${API_URL}/UnifiedAiAgent/sector-config/${sectorId}`, {
    headers: headers(token),
  });
  return res.data.data;
}

export async function saveSectorAiConfig(
  sectorId: number,
  dto: Omit<SectorAiConfigDTO, 'sectorId'>,
  token: string
): Promise<SectorAiConfigDTO> {
  const res = await axios.put(`${API_URL}/UnifiedAiAgent/sector-config/${sectorId}`, dto, {
    headers: headers(token),
  });
  return res.data.data;
}

export const CAPABILITY_LABELS: Record<AiAgentCapability, string> = {
  receptive: 'Receptive',
  campaign: 'Campaign',
  followup: 'Follow-up',
  crm: 'CRM',
};

export const CAPABILITY_SECTION_META: Record<
  AiAgentCapability,
  { title: string; description: string; usedIn: string }
> = {
  receptive: {
    title: 'Atendimento',
    description: 'Responde as conversas que chegam pelo WhatsApp.',
    usedIn: 'Conversas · funcionário padrão',
  },
  campaign: {
    title: 'Campanha',
    description: 'Responde depois de um disparo por WhatsApp ou e-mail.',
    usedIn: 'Campanhas · e-mail',
  },
  followup: {
    title: 'Follow-up',
    description: 'Avalia as regras e envia as mensagens agendadas.',
    usedIn: 'Regras de follow-up',
  },
  crm: {
    title: 'CRM',
    description: 'Move os cards de coluna depois de cada resposta da IA.',
    usedIn: 'Eventos das colunas do CRM',
  },
};